import React, { MutableRefObject, useCallback, useMemo, useState } from 'react';
import { MaybeNil } from '../../../utils/interfaces';
import { isNil } from 'lodash';
import { GraphQLUserAliasId } from '../../../utils/types';

interface IAliasNavigationContext {
  /**
   * Current state of regsitered aliases and their DOM elements
   */
  aliasedEntries: Record<
    GraphQLUserAliasId,
    MutableRefObject<HTMLDivElement | null>
  >;

  /**
   * Adds new aliasable element. Usually on mount or change of aliasId
   */
  addAliasedEntry: (
    aliasId: MaybeNil<GraphQLUserAliasId>,
    element: MutableRefObject<HTMLDivElement | null>,
  ) => void;

  /**
   * Remove (cleanup) new aliasable element. Usually on component unmount
   */
  removeAliasedEntry: (aliasId: MaybeNil<GraphQLUserAliasId>) => void;
}

export const AliasNavigationContext = React.createContext<
  IAliasNavigationContext | undefined
>(undefined);
AliasNavigationContext.displayName = 'AliasNavigationContext';

/**
 * Creates a context where builder aliasable elements (measures, steps, cohorts) can register/unregister
 * their DOM elements. Chart controls (Measure/Steps or Slice > Cohort) use these aliabsable
 * elements to perform navigation to them by scrolling into view and highlighting
 *
 * @param children - component tree that uses AliasNavigationContext
 */
export const AliasNavigationContextProvider = ({
  children,
}: React.PropsWithChildren<unknown>) => {
  const [aliasedEntries, setAliasedEntries] = useState<
    IAliasNavigationContext['aliasedEntries']
  >({});
  const addAliasedEntry: IAliasNavigationContext['addAliasedEntry'] =
    useCallback((aliasId, element) => {
      // we can't use immer because it will make value (Refs) readonly too which we don't want
      setAliasedEntries((prevState) => {
        // since aliasId is optional, we can avoid updating the state (and potentially save some renders)
        if (isNil(aliasId) || prevState.hasOwnProperty(aliasId)) {
          return prevState;
        } else {
          const newState = { ...prevState };
          newState[aliasId] = element;
          return newState;
        }
      });
    }, []);

  const removeAliasedEntry: IAliasNavigationContext['removeAliasedEntry'] =
    useCallback((aliasId) => {
      // we can't use immer because it will make value (Refs) readonly too which we don't want
      setAliasedEntries((prevState) => {
        // since aliasId is optional, we can avoid updating the state (and potentially save some renders)
        if (isNil(aliasId) || !prevState.hasOwnProperty(aliasId)) {
          return prevState;
        } else {
          const newState = { ...prevState };
          delete newState[aliasId];
          return newState;
        }
      });
    }, []);

  const contextValue = useMemo<IAliasNavigationContext>(
    () => ({
      aliasedEntries,
      addAliasedEntry,
      removeAliasedEntry,
    }),
    [addAliasedEntry, aliasedEntries, removeAliasedEntry],
  );

  return (
    <AliasNavigationContext.Provider value={contextValue}>
      {children}
    </AliasNavigationContext.Provider>
  );
};
